import {
  COOKIES_CONFIG,
  updateUserProperties,
  storeSessionContext,
} from '@belong/common';
import { nanoid } from 'nanoid';
import { parseCookies } from 'nookies';
import { UserModel } from 'src/api/models';
import { trackSiteVisitService } from 'src/api/platform';
import { setJwtToken, setTrackingId } from 'src/utils/api-client';
import { setJwtToken as setOctaneJwtToken } from 'src/utils/octane-client';
import { getExperimentsFromCookies } from './experiments';
import { setEnhancedConvertionData } from './tracking';

const { TRACKING_ID, JWT_TOKEN } = COOKIES_CONFIG;
const mixpanelApiKey = process.env.NEXT_PUBLIC_MIXPANEL_API_KEY;

// Only run this function once on the client.
let hasRunOnce = false;

export function initClient(user?: UserModel, partnerKey?: string) {
  if (!hasRunOnce) {
    hasRunOnce = true;

    const cookies = parseCookies();
    const trackingId = cookies[TRACKING_ID.name] || nanoid();
    const jwtToken = cookies[JWT_TOKEN.name];
    const experimentsFromCookies = getExperimentsFromCookies();

    // Campaign links carry a userUniqueId so the backend can join an anonymous visitor's journey to a known user.
    const userUniqueId =
      new URLSearchParams(window.location.search).get('uid') || undefined;

    const accessibilityPreferences = {
      theme_preference_str: window.matchMedia('(prefers-color-scheme: dark)')
        .matches
        ? 'dark'
        : 'light',
      language_preference_str: window.navigator.language,
      reduced_motion_preference_bool: window.matchMedia(
        '(prefers-reduced-motion: reduce)',
      ).matches,
      is_high_contrast_mode_preference_bool: window.matchMedia(
        '(forced-colors: active)',
      ).matches,
    };

    window.mixpanel?.init(mixpanelApiKey, {
      persistence: 'localStorage',
      api_host: `${window.location.origin}/mp`,
    });
    // TODO: FIgure out naming convention for multiple experiments!!
    window.mixpanel?.register({
      'First Visited URL': window.location.href,
      'First Visited Path': window.location.pathname,
      ...accessibilityPreferences,
      ...experimentsFromCookies,
    });

    if (user) {
      if (user.userId) {
        updateUserProperties({
          email: user.email,
          firstName: user.firstName,
          lastName: user.lastName,
          phone: user.phone,
          userType: user.type || 'homeowner',
          ...accessibilityPreferences,
          ...experimentsFromCookies,
          userId: user.userId,
          intercomId: user.intercomId,
        });

        storeSessionContext({
          ...accessibilityPreferences,
          ...experimentsFromCookies,
        });

        window.mixpanel?.identify?.(user.userId);
      }

      setEnhancedConvertionData(user);
    }

    if (!user?.userId && userUniqueId) {
      window.mixpanel?.identify?.(userUniqueId);
    }

    if (jwtToken) {
      setJwtToken(jwtToken);
      setOctaneJwtToken(jwtToken);
    }
    setTrackingId(trackingId);

    try {
      trackSiteVisitService(
        trackingId,
        partnerKey,
        experimentsFromCookies,
        userUniqueId,
      );
    } catch {
      // tracking failure is non-critical
    }
  }
}
